/**
 * Protected Route Component - SeeTex Web
 * 
 * SECURITY FEATURES:
 * - Validates authentication state before rendering
 * - Shows loading state during auth check to prevent flash of content
 * - Optionally requires email verification
 * - No sensitive content leakage during loading
 */

import { type ReactNode } from "react";
import { useAuth } from "../contexts/AuthContext";
import { LoginForm } from "./LoginForm";

interface ProtectedRouteProps {
    children: ReactNode;
    /** Require email verification before showing content */
    requireVerification?: boolean;
    /** Custom loading component */
    loadingComponent?: ReactNode;
}

export function ProtectedRoute({
    children,
    requireVerification = false,
    loadingComponent,
}: ProtectedRouteProps) {
    const { user, loading, isAuthenticated, isEmailVerified, resendVerificationEmail } = useAuth();

    // Show loading state during initial auth check
    if (loading) {
        return (
            loadingComponent || (
                <div className="auth-loading">
                    <div className="auth-loading-spinner" />
                    <p>Loading...</p>
                </div>
            )
        );
    }

    // Not authenticated - show login form
    if (!isAuthenticated) {
        return <LoginForm />;
    }

    // Require email verification if specified
    if (requireVerification && !isEmailVerified) {
        return (
            <div className="auth-verify-container">
                <div className="auth-verify-card">
                    <h2>Verify your email</h2>
                    <p>
                        We sent a verification link to <strong>{user?.email}</strong>.
                        Please check your inbox and click the link to verify your account.
                    </p>
                    <button
                        onClick={resendVerificationEmail}
                        className="auth-verify-resend"
                    >
                        Resend verification email
                    </button>
                    <p className="auth-verify-note">
                        Note: You may need to check your spam folder.
                    </p>
                </div>
            </div>
        );
    }

    // User is authenticated (and verified if required)
    return <>{children}</>;
}
